// English source strings of the codebase analysis (existing-project onboarding, spec
// docs/superpowers/specs/2026-09-28-existing-project-onboarding-design.md §1, §2.3, §5). Merged into `en`; pt-BR lives
// in pt-BR-onboarding.ts (complete, checked by the type). `onboarding.cause.<cause>` renders every `details.cause` of
// the analysis refusals (apps/api/src/routes/codebase-analysis.ts, apps/api/src/services/codebase-analysis.ts).
export const enOnboarding = {
  "onboarding.completion": "{percent}% complete",
  "onboarding.completionDetail": "{implemented} of {mandatory} mandatory · {partial} partial",
  "onboarding.codeColumn": "Code",
  "onboarding.notAnalysed": "Not analysed",
  "onboarding.verdict.implemented": "Implemented",
  "onboarding.verdict.partial": "Partial",
  "onboarding.verdict.not_found": "Not found",
  "onboarding.verdict.uncertain": "Uncertain",
  "onboarding.notHomologated": "not validated",
  "onboarding.moreRefs": "+{count}",
  "onboarding.refsLabel": "Files behind the verdict",

  "onboarding.title": "Codebase analysis",
  "onboarding.intro": "Checks in the repository which requirements are already implemented. Only file paths and line ranges are shown.",
  "onboarding.analyze": "Analyse the code",
  "onboarding.cancel": "Cancel",
  "onboarding.state.queued": "Queued",
  "onboarding.state.running": "Analysing",
  "onboarding.state.done": "Finished",
  "onboarding.state.failed": "Failed",
  "onboarding.state.cancelled": "Cancelled",
  "onboarding.counts": "{implemented} implemented · {partial} partial · {uncertain} uncertain · {not_found} not found · {closed} closed",
  "onboarding.runError": "The analysis stopped: {error}",
  "onboarding.runSelect": "Analysis run",
  "onboarding.cost": "Cost: {cost}",
  "onboarding.cause.disabled": "The codebase analysis is off in the configuration of this project or organization.",
  "onboarding.cause.not_attested": "The organization admin's declaration is missing (Integrations › Connections).",
  "onboarding.cause.no_repository": "This project has no repository prepared on the worker yet.",
  "onboarding.cause.no_jev_connection": "The organization has no Jev connection configured.",
  "onboarding.cause.unavailable": "The execution engine is unavailable right now; try again later.",
  "onboarding.cause.analysis_running": "The analysis is still running: answer once it has finished.",
  "onboarding.cause.no_machine_verdict": "This analysis has no verdict for the requirement: nothing to answer.",
  "onboarding.cause.no_evidence": "There is no evidence in the code for this requirement — run the analysis again or mark it as not ready.",
  "onboarding.cause.project_halted": "The project is paused: nothing is closed while it stays paused.",

  "onboarding.review": "To review",
  "onboarding.reviewEmpty": "Nothing to review.",
  "onboarding.reason.uncertain": "Jev could not tell",
  "onboarding.reason.would_close": "Jev says it is already implemented",
  "onboarding.openTasks": "Open tasks",
  "onboarding.ready": "Ready",
  "onboarding.notReady": "Not ready",
  "onboarding.readyFor": "Ready — {code}",
  "onboarding.notReadyFor": "Not ready — {code}",
  "onboarding.skippedTitle": "These tasks were left as they were",
  "onboarding.skip.invalid_transition": "it is no longer in Backlog, In analysis or To do (only work not started yet is closed)",
  "onboarding.skip.children_pending": "it has required subtasks still open",
  "onboarding.skip.gate_pending": "it has required checks still pending",
  "onboarding.skip.dependencies_pending": "it depends on tasks not finished yet",
  "onboarding.skip.shared_requirement_open": "it also serves another requirement not covered yet",
  "onboarding.skip.touched": "it was changed after the analysis closed it",
  "onboarding.skip.not_existing": "it is no longer closed as already existing",
  "onboarding.answeredReady": "{code}: ready. {count} task(s) closed.",
  "onboarding.answeredNotReady": "{code}: not ready. Its tasks stay open.",
  "onboarding.dryRun": "I would close these {count} tasks",
  "onboarding.dryRunBody": "Automatic closing was off for this analysis: nothing was closed. Answer Ready on a requirement to close its tasks.",

  "onboarding.closed": "Closed by the analysis",
  "onboarding.closedEmpty": "The analysis closed no task.",
  "onboarding.reopen": "Reopen",
  "onboarding.reopenFor": "Reopen — task #{number}",
  "onboarding.reopenAll": "Reopen the tasks closed by this analysis",
  "onboarding.reopenAllBody":
    "Every task this analysis closed that nobody changed since goes back to the board, and its requirement stops counting as implemented. Tasks changed after the closure stay as they are.",
  "onboarding.reopenedCount": "{count} task(s) reopened.",
  "onboarding.taskReopened": "Task #{number} reopened.",

  "onboarding.existingPill": "Already existed",
  "onboarding.reopenExistingBody": "This task was closed because the code already existed. Reopening it sends it back to the board.",
  "onboarding.startedDependents": "These tasks depend on it and have already started; they will not be paused",

  "onboarding.attestation.title": "Codebase analysis: sending code to a third party",
  "onboarding.attestation.text":
    "When the codebase analysis is on, repository excerpts — up to 6000 characters per requirement, without secret files and with sensitive data masked — are sent to OpenRouter for the Jev model to judge whether each requirement is already implemented.",
  "onboarding.attestation.checkbox": "I declare that the organization has the right to send this code to this third party.",
  "onboarding.attestation.submit": "Record declaration",
  "onboarding.attestation.by": "Declared by {name} on {date}",
  "onboarding.attestation.revoke": "Revoke",
  "onboarding.attestation.recorded": "Declaration recorded.",
  "onboarding.attestation.revoked": "Declaration revoked: no new analysis starts until it is recorded again.",

  "production.doneOfExisting": "{done} of {total} ({existing} already existed)",

  // GitHub onboarding (part B, spec §3): `githubOnboarding.error.<code>` has one entry per PROVISION_ERRORS code.
  "githubOnboarding.newFromGithub": "New project from GitHub",
  "githubOnboarding.createDescription": "Pick a repository your GitHub connection reaches: the project takes its name and default branch, then it is cloned.",
  "githubOnboarding.connectTitle": "Connect repository",
  "githubOnboarding.connectDescription": "Pick this project's repository on GitHub: it is cloned, its test command detected and the project validated.",
  "githubOnboarding.connection": "GitHub connection",
  "githubOnboarding.noConnection": "There is no active GitHub connection yet.",
  "githubOnboarding.connectGithub": "Connect GitHub",
  "githubOnboarding.repository": "Repository",
  "githubOnboarding.filter": "Filter repositories",
  "githubOnboarding.private": "Private",
  "githubOnboarding.defaultBranch": "default branch {branch}",
  "githubOnboarding.noRepositories": "This connection reaches no repository.",
  "githubOnboarding.noMatch": "No repository matches the filter.",
  "githubOnboarding.create": "Create project",
  "githubOnboarding.connect": "Connect",
  "githubOnboarding.created": "Project {name} created: cloning the repository.",
  "githubOnboarding.cardBody": "This project has no local copy of its repository yet. Connect it from GitHub: it is cloned, its test command detected and the project validated.",
  "githubOnboarding.adminOnly": "Only an organization admin can connect a repository.",
  "githubOnboarding.step.cloning": "Cloning",
  "githubOnboarding.step.detecting": "Detecting",
  "githubOnboarding.step.validating": "Validating",
  "githubOnboarding.stepState.done": "done",
  "githubOnboarding.stepState.current": "in progress",
  "githubOnboarding.stepState.pending": "pending",
  "githubOnboarding.failed": "The repository could not be connected",
  "githubOnboarding.retry": "Try again",
  "githubOnboarding.testCommand": "Test command: {command}",
  "githubOnboarding.noTestCommand": "We did not find the test command — set it in the settings",
  "githubOnboarding.checkingReadiness": "Checking readiness…",
  "githubOnboarding.stillChecking": "Readiness is still being checked — try Validate now in a moment.",
  "githubOnboarding.operatorPending": "Execution policy: waiting for the operator",
  "githubOnboarding.error.too_large": "The repository is too large to clone here (limit 1 GB).",
  "githubOnboarding.error.disk_low": "The server does not have enough free disk space to clone this repository.",
  "githubOnboarding.error.timeout": "Cloning took longer than 10 minutes and was stopped.",
  "githubOnboarding.error.host_not_allowed": "This repository address is not allowed: only GitHub repositories over HTTPS.",
  "githubOnboarding.error.auth_failed": "GitHub refused access: check that the connection still reaches this repository.",
  "githubOnboarding.error.not_found": "The repository was not found on GitHub: it may have been renamed, deleted or removed from the connection.",
  "githubOnboarding.error.base_branch_missing": "The repository is empty or has no default branch.",
  "githubOnboarding.error.clone_failed": "Cloning the repository failed. Try again.",
  "githubOnboarding.error.connection_unavailable": "The service that clones repositories is unavailable right now. Try again in a moment.",
  "githubOnboarding.error.not_git_root": "The cloned folder is not the root of a git repository.",
} as const;
